/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  ArrowRight, 
  Users, 
  Calendar, 
  Award, 
  TrendingUp,
  Handshake,
  Globe,
  Building,
  Star,
  ChevronRight,
  MapPin,
  Utensils,
  Briefcase,
  Heart,
  Home,
  DollarSign,
  Megaphone,
  UsersRound,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatEventDateRange } from './EventDetailPage';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_WHITE = '/brand/logo-white.png';

// Icons available for editable feature buttons in the Welcome section.
const FEATURE_ICONS = {
  users:        { Icon: Users,       tone: '#002249' },
  calendar:     { Icon: Calendar,    tone: '#E63946' },
  award:        { Icon: Award,       tone: '#457B9D' },
  handshake:    { Icon: Handshake,   tone: '#002249' },
  'trending-up':{ Icon: TrendingUp,  tone: '#E63946' },
  globe:        { Icon: Globe,       tone: '#457B9D' },
  building:     { Icon: Building,    tone: '#002249' },
  star:         { Icon: Star,        tone: '#E63946' },
  briefcase:    { Icon: Briefcase,   tone: '#002249' },
  heart:        { Icon: Heart,       tone: '#E63946' },
};

// Category icons mapping
const categoryIcons = {
  restaurants: Utensils,
  professional: Briefcase,
  health: Heart,
  real_estate: Home,
  finance: DollarSign,
  marketing: Megaphone,
  community: UsersRound
};

const HomePage = () => {
  const [events, setEvents] = useState([]);
  const [featuredMembers, setFeaturedMembers] = useState([]);
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);  

  // Listen for cross-frame preview updates from AdminHomepageManager.
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type === 'dccf-homepage-preview' && e.data.content) {
        setContent(e.data.content);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, featuredRes, homeRes] = await Promise.all([
        axios.get(`${API}/events?featured=true&limit=3`),
        axios.get(`${API}/featured-members`),
        axios.get(`${API}/homepage`),
      ]);
      setEvents(eventsRes.data);
      setFeaturedMembers(featuredRes.data);
      setContent(homeRes.data);
    } catch {
      // error handled silently
    }
  };

  const sectionEnabled = (k) => content?.sections?.[k]?.enabled !== false;
  const welcome = content?.welcome || {};
  const video = content?.video || {};
  const youtubeId = video.youtube_id || 'iyVy8761cLE';

  const benefits = [
    { icon: Handshake, title: 'Networking Opportunities', description: 'Connect with fellow Dominican entrepreneurs and business leaders across Florida.' },
    { icon: TrendingUp, title: 'Business Visibility', description: 'Get featured in our member directory and marketing materials.' },
    { icon: Calendar, title: 'Exclusive Events', description: 'Access to networking events, workshops, and our annual gala.' },
    { icon: Megaphone, title: 'Marketing Exposure', description: 'Promote your business through our channels and partnerships.' },
    { icon: Award, title: 'Community Recognition', description: 'Be recognized as part of an elite business community.' },
    { icon: Globe, title: 'Strategic Connections', description: 'Build bridges between Florida and the Dominican Republic.' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      {sectionEnabled('hero') && (
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background — Split images (no per-side color overlay; unified blue .hero-overlay tints both) */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2">
            <div className="relative hidden md:block">
              <Img variant="generic"
                src="https://images.unsplash.com/photo-1673985870997-0e2aaae8cbb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxzYW50byUyMGRvbWluZ28lMjBkb21pbmljYW4lMjByZXB1YmxpY3xlbnwwfHx8fDE3NzU4NTI0NTl8MA&ixlib=rb-4.1.0&q=85"
                alt="Santo Domingo"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative">
              <Img variant="generic"
                src="https://images.unsplash.com/photo-1748457230066-8ec3c959dfd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtaWFtaSUyMGZsb3JpZGElMjBza3lsaW5lJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc1ODUyNDU5fDA&ixlib=rb-4.1.0&q=85"
                alt="Miami Florida"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Animated Ribbon accents — architectural background identity (not decoration).
            Paths extend beyond the SVG viewBox and the container clips edges (overflow-hidden),
            so lines never appear cut. Mobile hides the secondary/tertiary layers to preserve
            breathing room and performance. */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Top-right — 2 widely-spaced flowing arcs */}
          <svg
            className="absolute -top-32 -right-32 sm:-top-24 sm:-right-24 w-[130%] sm:w-[900px] h-[900px] opacity-[0.08]"
            viewBox="-50 -50 800 800" fill="none"
            aria-hidden="true"
          >
            <path
              d="M-50 80 C 200 180 420 280 620 360 S 850 460 900 500"
              stroke="#E63946" strokeWidth="1.5" strokeLinecap="round"
              className="ribbon-flow-1"
            />
            <path
              d="M-100 260 C 150 340 380 420 600 500 S 820 580 900 620"
              stroke="white" strokeWidth="1" strokeLinecap="round"
              className="ribbon-flow-2 hidden sm:block"
            />
          </svg>

          {/* Bottom-left — single elegant curve */}
          <svg
            className="absolute -bottom-24 -left-24 w-[120%] sm:w-[780px] h-[780px] opacity-[0.07] hidden sm:block"
            viewBox="-40 -40 700 700" fill="none"
            aria-hidden="true"
          >
            <path
              d="M-40 560 C 140 460 340 360 520 300 S 640 230 700 200"
              stroke="#1D3557" strokeWidth="1.25" strokeLinecap="round"
              className="ribbon-flow-3"
            />
          </svg>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container-custom pt-24 sm:pt-28 pb-16">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-xs sm:text-sm font-semibold tracking-wider uppercase mb-5 sm:mb-6 rounded-md border border-white/10">
                Dominican Chamber of Commerce of Florida
              </span>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5 sm:mb-6">
                Bridging Commerce Between
                <span className="block text-gradient-bridge mt-1">Two Great Nations</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/80 mb-8 leading-relaxed max-w-2xl">
                Empowering Dominican entrepreneurs and professionals in Florida through unity, 
                leadership, and strategic connections. Join our premier business network.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/membership" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-white text-[#002249] hover:bg-white/90 font-semibold px-8 rounded-md shadow-lg"
                    data-testid="hero-become-member-btn"
                  >
                    Become a Member
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/leadership" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-[#002249] font-semibold px-8 rounded-md bg-transparent"
                    data-testid="hero-leadership-btn"
                  >
                    Join Leadership
                  </Button>
                </Link>
                <Link to="/events" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full sm:w-auto border-2 border-white/50 text-white hover:bg-white/10 font-semibold px-8 rounded-md bg-transparent"
                    data-testid="hero-events-btn"
                  >
                    View Events
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/80 rounded-full" />
          </div>
        </motion.div>
      </section>
      )}

      {/* Animated Ribbon Divider */}
      <div className="relative h-6 overflow-hidden bg-white">
        <svg className="absolute inset-0 w-[110%] h-full section-ribbon-animated" preserveAspectRatio="none" viewBox="0 0 1600 24">
          <path d="M0 12C200 4 400 20 600 12S1000 4 1200 12S1400 20 1600 12" stroke="#E63946" strokeWidth="2.5" fill="none" opacity="0.35" />
          <path d="M0 8C267 18 533 2 800 12S1200 22 1600 8" stroke="#1D3557" strokeWidth="2" fill="none" opacity="0.25" />
          <path d="M0 16C300 6 600 22 900 12S1300 2 1600 16" stroke="white" strokeWidth="1.5" fill="none" opacity="0.2" />
          <path d="M0 20C200 10 500 22 800 14S1100 6 1600 18" stroke="#E63946" strokeWidth="1.5" fill="none" opacity="0.2" />
          <path d="M0 4C400 14 700 2 1000 10S1300 18 1600 6" stroke="#457B9D" strokeWidth="1" fill="none" opacity="0.15" />
        </svg>
      </div>

      {/* Welcome Guide Section */}
      {sectionEnabled('welcome') && welcome.enabled !== false && (
      <section className="section-padding bg-white" data-testid="welcome-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
          >
            <div>
              <span className="text-xs sm:text-sm font-bold text-[#E63946] tracking-wider uppercase mb-3 block" data-testid="welcome-label">
                {welcome.label || 'Welcome to DCCF'}
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249] mb-5" data-testid="welcome-headline">
                {welcome.headline || "Your Gateway to Success in Florida's Business Community"}
              </h2>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-6" data-testid="welcome-description">
                {welcome.description || "Welcome to the Dominican Chamber of Commerce of Florida! We're delighted to have you here. Our platform is designed to help you connect, grow, and succeed in the vibrant Florida business landscape."}
              </p>
              <div className="space-y-4">
                {(welcome.features || []).filter((f) => f.enabled !== false).map((f, i) => {
                  const meta = FEATURE_ICONS[f.icon] || FEATURE_ICONS.star;
                  const Icon = meta.Icon;
                  const tint = meta.tone;
                  const Wrapper = f.url ? Link : 'div';
                  const wrapProps = f.url ? { to: f.url } : {};
                  return (
                    <Wrapper
                      key={f.id || i}
                      {...wrapProps}
                      className={`flex items-start gap-4 ${f.url ? 'group cursor-pointer' : ''}`}
                      data-testid={`welcome-feature-${i}`}
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${tint}1a` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: tint }} />
                      </div>
                      <div>
                        <h4 className={`font-semibold text-[#002249] text-[15px] ${f.url ? 'group-hover:text-[#E63946] transition-colors' : ''}`}>
                          {f.title}
                        </h4>
                        {f.description && <p className="text-slate-600 text-sm">{f.description}</p>}
                      </div>
                    </Wrapper>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <Img variant="event"
                  src={resolveImg(welcome.image_url) || '/brand/flyer.png'}
                  alt="DCCF"
                  className="w-full h-auto object-contain"
                  data-testid="welcome-image"
                />
              </div>
              {welcome.stat?.enabled !== false && (welcome.stat?.number || welcome.stat?.label) && (
                <div className="absolute -bottom-4 -left-4 sm:-bottom-5 sm:-left-5 bg-[#002249] text-white p-4 sm:p-5 rounded-xl shadow-xl" data-testid="welcome-stat-badge">
                  <p className="text-2xl sm:text-3xl font-bold">{welcome.stat?.number || '500+'}</p>
                  <p className="text-white/80 text-xs sm:text-sm">{welcome.stat?.label || 'Active Members'}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
      )}

      {/* Brand Essence Section */}
      {sectionEnabled('brand_essence') && (
      <section className="section-padding bg-[#002249] relative overflow-hidden" data-testid="brand-essence-section">
        {/* Subtle Dominican flag accent */}
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute top-0 left-0 w-[130%] h-56 opacity-[0.05] section-ribbon-animated" preserveAspectRatio="none" viewBox="-50 0 1700 240" aria-hidden="true">
            <path d="M-50 120 C 380 30 780 200 1200 100 S 1600 120 1700 110" stroke="#CD1125" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            <path d="M-50 80  C 320 160 680 40  1080 110 S 1500 160 1700 90"  stroke="white"   strokeWidth="1"   strokeLinecap="round" fill="none" />
          </svg>
          {/* White cross hint */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1px] h-3/4 bg-white/[0.03]" />
          <div className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-[1px] bg-white/[0.03]" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <Img variant="generic" src={LOGO_WHITE} alt="DCCF" className="h-24 sm:h-32 lg:h-36 mx-auto mb-8 object-contain" data-testid="brand-logo-display" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
              The Dominican Chamber of Commerce of Florida
            </h2>
            <p className="text-white/55 text-base sm:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              A premier institutional force connecting Dominican enterprise with the vast opportunities 
              of the American market — through vision, authority, and legacy.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Link to="/about">
                <Button size="lg" className="w-full sm:w-auto bg-[#CD1125] hover:bg-[#b00f20] text-white font-semibold px-8 rounded-sm shadow-lg shadow-[#CD1125]/20" data-testid="discover-essence-btn">
                  Discover Our Essence
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/membership">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 font-semibold px-8 rounded-sm bg-transparent" data-testid="join-brand-btn">
                  Join the Chamber
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
        {/* Dominican flag abstract bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#002249] via-[#CD1125] to-[#002249]" />
      </section>
      )}

      {/* Brand Video Section */}
      {sectionEnabled('video') && video.enabled !== false && (
      <section className="relative overflow-hidden" data-testid="brand-video-home"
        style={{ background: 'linear-gradient(to bottom, #0A1628, #0D1B2E 60%, #162A42 100%)' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-[#002249]/30 via-transparent to-transparent pointer-events-none z-10" />
        <div className="container-custom py-14 sm:py-20 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-10"
          >
            <span className="text-xs sm:text-sm font-bold text-[#CD1125] tracking-[0.2em] uppercase mb-3 block" data-testid="video-label">
              {video.label || 'The Chamber Experience'}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3" data-testid="video-headline">
              {video.headline || 'Experience Our Brand'}
            </h2>
            <p className="text-white/40 text-sm sm:text-base max-w-xl mx-auto" data-testid="video-description">
              {video.description || 'Discover the vision, impact, and community that define the DCCF.'}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="max-w-5xl mx-auto"
          >
            <div className="relative aspect-video rounded-sm overflow-hidden shadow-2xl shadow-black/50 border border-white/10" data-testid="home-video-player">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1&color=white`}
                title={video.headline || 'The Chamber Experience'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </motion.div>
          {video.cta_text && (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center mt-8"
            >
              <Link to={video.cta_url || '/about'}>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-sm bg-transparent" data-testid="experience-brand-btn">
                  <Play className="mr-2 w-4 h-4" /> {video.cta_text}
                </Button>
              </Link>
            </motion.div>
          )}
        </div>
      </section>
      )}

      {/* ═══════════════════════════════════════════════════════════
          FEATURED MEMBER COMPANIES — Luxury White Stage
          Dominican Ribbon Architecture + Soft Depth
          Unified transition from dark navy → warm ivory
          ═══════════════════════════════════════════════════════════ */}
      {featuredMembers.length > 0 && sectionEnabled('featured_members') && (
        <section className="relative overflow-hidden" data-testid="featured-members-section">

          {/* ── GRADIENT TRANSITION from navy to ivory (the key unifier) ── */}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(180deg, #162A42 0%, #1E3650 4%, #2C4A64 8%, #4A6A82 14%, #7A9AAF 20%, #A8C0CE 26%, #D0DAE0 32%, #E5E8E8 38%, #EDE9E4 44%, #F2EFE9 50%, #F5F2EE 60%, #F2EFE9 75%, #FEFEFE 100%)'
          }} />

          {/* ── Stage spotlight — center illumination ── */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-[30%] left-1/2 -translate-x-1/2 w-[1200px] h-[600px]" style={{
              background: 'radial-gradient(ellipse 60% 50% at 50% 40%, rgba(255,255,255,0.7) 0%, transparent 70%)'
            }} />
          </div>

          {/* ── Vignette edges for depth ── */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse 75% 65% at 50% 55%, transparent 40%, rgba(0,0,0,0.02) 100%)'
          }} />

          {/* ── Red ambient glow — top right ── */}
          <div className="absolute top-[10%] -right-32 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(205,17,37,0.05) 0%, transparent 65%)'
          }} />

          {/* ── Blue ambient glow — bottom left ── */}
          <div className="absolute bottom-[5%] -left-32 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(0,34,73,0.05) 0%, transparent 65%)'
          }} />

          {/* ── Dominican Ribbon Architecture ── */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1600 1000" fill="none" preserveAspectRatio="none">
            <path d="M-60 300 C200 240, 500 360, 800 300 S1200 200, 1660 300" stroke="#CD1125" strokeWidth="1.8" opacity="0.07" />
            <path d="M-60 330 C250 270, 550 390, 850 330 S1250 230, 1660 330" stroke="#CD1125" strokeWidth="1" opacity="0.04" />
            <path d="M-60 550 C300 490, 600 610, 900 550 S1300 470, 1660 550" stroke="#94A3B8" strokeWidth="1.5" opacity="0.06" />
            <path d="M-60 580 C350 520, 650 640, 950 580 S1350 500, 1660 580" stroke="#CBD5E1" strokeWidth="1" opacity="0.04" />
            <path d="M-60 780 C200 720, 500 840, 800 780 S1200 700, 1660 780" stroke="#002249" strokeWidth="1.8" opacity="0.06" />
            <path d="M-60 810 C250 750, 550 870, 850 810 S1250 730, 1660 810" stroke="#1D3557" strokeWidth="1" opacity="0.03" />
          </svg>

          {/* ── Floor reflection line ── */}
          <div className="absolute bottom-[30%] left-[10%] right-[10%] h-px pointer-events-none" style={{
            background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.03) 30%, rgba(0,0,0,0.04) 50%, rgba(0,0,0,0.03) 70%, transparent)'
          }} />

          {/* ── Inner content with generous top padding for the gradient zone ── */}
          <div className="pt-32 sm:pt-44 pb-20 sm:pb-28">

            {/* Section header */}
            <div className="text-center px-6 mb-12 sm:mb-16 relative z-10">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <div className="flex items-center justify-center gap-4 mb-5">
                  <div className="h-px w-10 sm:w-16" style={{ background: 'linear-gradient(to right, transparent, rgba(205,17,37,0.3))' }} />
                  <span className="text-[10px] sm:text-xs font-bold text-[#CD1125] tracking-[0.3em] uppercase">Our Members</span>
                  <div className="h-px w-10 sm:w-16" style={{ background: 'linear-gradient(to left, transparent, rgba(205,17,37,0.3))' }} />
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249] mb-4 tracking-tight">Featured Member Companies</h2>
                <p className="text-[#002249]/40 text-sm sm:text-base max-w-lg mx-auto leading-relaxed">A curated selection of the businesses that power our Chamber community.</p>
              </motion.div>
            </div>

            {/* Carousel track */}
            <div className="relative group overflow-hidden" data-testid="carousel-wrapper" style={{ contain: 'layout paint' }}>
              <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 lg:w-48 z-10 pointer-events-none"
                style={{ background: 'linear-gradient(to right, #F4F1EC, rgba(244,241,236,0))' }} />
              <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 lg:w-48 z-10 pointer-events-none"
                style={{ background: 'linear-gradient(to left, #F4F1EC, rgba(244,241,236,0))' }} />

              <div className="flex items-center gap-12 sm:gap-16 lg:gap-20 py-8 group-hover:[animation-play-state:paused]"
                style={{
                  animation: `logoScroll ${Math.max(30, featuredMembers.length * 4)}s linear infinite`,
                  width: 'max-content',
                  willChange: 'transform',
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden'
                }}
                data-testid="carousel-track">
                {[...featuredMembers, ...featuredMembers, ...featuredMembers].map((m, i) => {
                  const logoSrc = m.logo_url ? (resolveImg(m.logo_url)) : null;
                  return (
                    <div key={`logo-${i}`} className="flex-shrink-0 flex items-center justify-center" data-testid={`featured-logo-${i}`}>
                      {logoSrc ? (
                        <Img variant="generic" src={logoSrc} alt={m.company_name}
                          className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain transition-all duration-500 hover:scale-110"
                          loading="eager"
                          decoding="async"
                          width="96"
                          height="96"
                          style={{ opacity: 0.65, filter: 'grayscale(10%) drop-shadow(0 4px 12px rgba(0,0,0,0.08))', transition: 'filter 0.5s, opacity 0.5s, transform 0.5s' }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.filter = 'grayscale(0%) drop-shadow(0 8px 24px rgba(0,0,0,0.12))'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.65'; e.currentTarget.style.filter = 'grayscale(10%) drop-shadow(0 4px 12px rgba(0,0,0,0.08))'; }} />
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 flex flex-col items-center justify-center transition-opacity duration-500"
                          style={{ opacity: 0.25 }}>
                          <Building className="w-8 h-8 sm:w-10 sm:h-10 text-[#002249]" />
                          <span className="text-[7px] sm:text-[8px] text-[#002249] text-center mt-1 max-w-[70px] truncate font-medium">{m.company_name}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Decorative closure */}
            <div className="flex items-center justify-center mt-12 sm:mt-16 px-6 relative z-10">
              <div className="h-px w-10 sm:w-16" style={{ background: 'linear-gradient(to right, transparent, rgba(205,17,37,0.2))' }} />
              <div className="w-1.5 h-1.5 mx-3 rotate-45 rounded-[1px]" style={{ backgroundColor: 'rgba(205,17,37,0.15)' }} />
              <div className="h-px w-10 sm:w-16" style={{ background: 'linear-gradient(to left, transparent, rgba(0,34,73,0.2))' }} />
            </div>
          </div>
        </section>
      )}

      {/* Membership Benefits */}
      {sectionEnabled('benefits') && (
      <section className="section-padding bg-[#002249] relative overflow-hidden" data-testid="benefits-section">
        {/* Animated ribbon accents */}
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute top-0 left-0 w-[130%] h-40 opacity-[0.06] section-ribbon-animated" preserveAspectRatio="none" viewBox="-50 0 1700 140" aria-hidden="true">
            <path d="M-50 70  C 260 20 560 110 900 65  S 1400 20 1700 70" stroke="#E63946" strokeWidth="1.25" strokeLinecap="round" fill="none" />
            <path d="M-50 100 C 300 130 640 50  1000 95 S 1500 130 1700 95" stroke="#457B9D" strokeWidth="1"    strokeLinecap="round" fill="none" />
          </svg>
          <svg className="absolute bottom-0 right-0 w-[130%] h-40 opacity-[0.05] section-ribbon-animated hidden sm:block" style={{animationDelay: '-12s'}} preserveAspectRatio="none" viewBox="-50 0 1700 140" aria-hidden="true">
            <path d="M-50 80 C 320 40 680 115 1060 70 S 1450 35 1700 75" stroke="white"  strokeWidth="1"   strokeLinecap="round" fill="none" />
          </svg>
        </div>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-12"
          >
            <span className="text-xs sm:text-sm font-bold text-[#E63946] tracking-wider uppercase mb-3 block">
              Membership Benefits
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Why Join the Chamber?
            </h2>
            <p className="text-white/65 text-sm sm:text-base max-w-2xl mx-auto">
              Unlock a world of opportunities designed to accelerate your business growth and expand your professional network.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-5 sm:p-6 rounded-xl hover:bg-white/10 transition-colors"
                data-testid={`benefit-${index}`}
              >
                <benefit.icon className="w-9 h-9 text-[#E63946] mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Link to="/membership">
              <Button size="lg" className="bg-white text-[#002249] hover:bg-white/90 font-semibold px-8 rounded-md shadow-lg" data-testid="join-now-btn">
                Join Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Events Preview */}
      {sectionEnabled('events') && (
      <section className="section-padding bg-white" data-testid="events-preview">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 sm:mb-12 gap-4"
          >
            <div>
              <span className="text-xs sm:text-sm font-bold text-[#E63946] tracking-wider uppercase mb-3 block">
                Upcoming Events
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#002249]">
                Connect & Grow With Us
              </h2>
            </div>
            <Link to="/events">
              <Button variant="outline" className="border-[#002249] text-[#002249] hover:bg-[#002249] hover:text-white rounded-md" data-testid="view-all-events-btn">
                View All Events
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {events.length > 0 ? events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                data-testid={`event-card-${index}`}
              >
                <Link
                  to={`/events/${event.id}`}
                  className="group block bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#002249]/40 hover:shadow-md transition-all"
                  data-testid={`event-card-link-${index}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-[#002249] to-[#457B9D] relative overflow-hidden">
                    {event.image_url ? (
                      <Img variant="event" src={event.image_url} alt={event.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Calendar className="w-14 h-14 text-white/30" />
                      </div>
                    )}
                    {event.is_featured && (
                      <span className="absolute top-3 right-3 bg-[#E63946] text-white text-xs font-bold px-3 py-1 rounded-md">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center text-sm text-slate-500 mb-2.5">
                      <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">
                        {formatEventDateRange(event.date, event.end_date)}{event.time ? ` · ${event.time}` : ''}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#002249] mb-2 group-hover:text-[#E63946] transition-colors leading-snug">
                      {event.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-3 line-clamp-2 leading-relaxed">{event.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-slate-500 min-w-0 flex-1">
                        <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{event.location}</span>
                      </div>
                      <span className="flex-shrink-0 ml-2 text-[#002249] opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center text-sm font-semibold">
                        View <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            )) : (
              <div className="col-span-full text-center py-12 text-slate-500">
                <Calendar className="w-14 h-14 mx-auto mb-4 text-slate-300" />
                <p className="text-base">No upcoming events at the moment. Check back soon!</p>
              </div>
            )}
          </div>
        </div>
      </section>
      )}


      {/* CTA Section */}
      {sectionEnabled('cta_footer') && (
      <section className="section-padding bg-gradient-to-r from-[#1D3557] via-[#002249] to-[#E63946] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 pointer-events-none">
          <svg className="absolute top-0 left-0 w-[130%] h-full opacity-[0.05] section-ribbon-animated" viewBox="-50 0 1700 440" preserveAspectRatio="none" aria-hidden="true">
            <path d="M-50 160 C 260 90  560 260 920 170 S 1400 90  1700 190" stroke="white"   strokeWidth="1.25" strokeLinecap="round" fill="none" />
            <path d="M-50 280 C 340 200 680 350 1020 240 S 1480 300 1700 240" stroke="white"   strokeWidth="1"    strokeLinecap="round" fill="none" />
            <path d="M-50 360 C 320 260 640 400 960 310  S 1450 240 1700 310" stroke="#E63946" strokeWidth="1"    strokeLinecap="round" fill="none" className="hidden sm:inline" />
          </svg>
        </div>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-5">
              Ready to Grow Your Business?
            </h2>
            <p className="text-white/80 text-base sm:text-lg mb-8 leading-relaxed">
              Join the Dominican Chamber of Commerce of Florida today and become part of 
              a thriving community of entrepreneurs and professionals.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Link to="/membership" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-white text-[#002249] hover:bg-white/90 font-semibold px-8 rounded-md shadow-lg" data-testid="cta-member-btn">
                  Become a Member
                </Button>
              </Link>
              <Link to="/contact" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-[#002249] font-semibold px-8 rounded-md bg-transparent" data-testid="cta-contact-btn">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      )}
    </div>
  );
};

export default HomePage;
